import { ui } from "@/components/ui";
import { Badge } from "@/components/ui/badge";
import { getSupabaseStorageUrl } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { time } from "@/services/time";
import type { StatefulDiaryT } from "@/types/database/diaries";
import { useAuth } from "@/utils/auth";
import { Link } from "@tanstack/react-router";
import { ArrowRight, BellRing } from "lucide-react";
import type React from "react";
import DiaryProcessInfo from "../features/overview/components/DiaryProcessInfo";
import DiaryTypeLink from "./DiaryTypeLink";
import MissingDiaryDetails from "./MissingDiaryDetails";

interface DiaryCardProps {
	diary: StatefulDiaryT;
}

//
const DiaryOverviewCard: React.FC<DiaryCardProps> = ({ diary }) => {
	const auth = useAuth();
	const storageBaseUrl = getSupabaseStorageUrl();

	// Construct full logo URLs
	const organisationLogoUrl = diary.organisation_logo
		? `${storageBaseUrl}organisation-logos/${diary.organisation_logo}`
		: null;
	const diaryTypeLogoUrl = diary.diary_type_logo
		? `${storageBaseUrl}diary-type-logos/${diary.diary_type_logo}`
		: null;

	return (
		<ui.Card className={cn("mb-4 text-sm overflow-hidden relative")}>
			<ui.CardHeader className="pt-2 pb-2">
				<div className="flex justify-between items-center pr-2 w-full">
					<div className="flex justify-start items-center gap-2 my-0 py-0 w-full">
						<DiaryOverviewLogo
							logoUrl={organisationLogoUrl as string}
							altText={diary.organisation_name || "Diary type"}
							label="Organisation"
						/>
						{diaryTypeLogoUrl && (
							<>
								<p className="text-slate-700 text-2xl rotate-45">+</p>
								<DiaryOverviewLogo
									logoUrl={diaryTypeLogoUrl as string}
									altText={diary.diary_type || "Diary type"}
									label="Diary"
								/>
							</>
						)}
					</div>
					{diary.tracker_due && (
						<div className="my-auto animate-big-wiggle">
							<BellRing className="text-primary" size={24} />
						</div>
					)}
				</div>
			</ui.CardHeader>
			<ui.CardContent className="space-y-4 pt-4 border-slate-200 border-t">
				{/* Organisation and Diary Type Section */}
				<div className="flex justify-between items-center">
					<h2 className="font-medium text-slate-900 text-xl">
						{diary.diary_name}
					</h2>
					<h4 className="font-normal text-slate-700 text-sm">
						{time.formatDate(diary?.start_date)}{" "}
						{diary?.end_date ? `- ${time.formatDate(diary.end_date)}` : "- Now"}
					</h4>
				</div>
				{/* Patient Info Badge */}
				{diary.patient_id !== auth.id && (
					<ui.Badge className="mt-2" variant="outline">
						{diary.patient_info.patient_name}
					</ui.Badge>
				)}
				<DiaryProcessInfo diary={diary} />
				<MissingDiaryDetails diary={diary} />
			</ui.CardContent>
			<ui.CardFooter className="flex flex-col justify-start items-center gap-2 gap-y-4 pt-4 border-slate-200 border-t">
				<DiaryTypeLink diaryType={diary.diary_type as string} />

				<ui.Button asChild>
					<Link
						data-testid="diary-overview-button"
						to="/diaries/$diaryId/home/new"
						params={{ diaryId: diary.diary_id }}
					>
						Next
						<ArrowRight className="ml-2 w-5 h-5" />
					</Link>
				</ui.Button>
			</ui.CardFooter>
		</ui.Card>
	);
};

export default DiaryOverviewCard;
const DiaryOverviewLogo = ({
	logoUrl,
	altText,
}: { logoUrl: string; altText: string; label: string }) => {
	return (
		<div className="flex justify-start items-center h-[60px]">
			{logoUrl ? (
				<img
					src={logoUrl}
					alt={`${altText || "Diary type"} logo`}
					className="max-w-[150px] h-[60px] object-contain"
				/>
			) : (
				<Badge className="font-medium">{altText || "N/A"}</Badge>
			)}
		</div>
	);
};
